import BButton from 'components/common/BButton';
import RatingStar from 'components/common/RatingStar';
import VehicleDetailCommentsItem from 'components/Vehicle/VehicleDetail/VehicleDetailCommentsItem';
import VehicleDetailCommentsSubAverageItem from 'components/Vehicle/VehicleDetail/VehicleDetailCommentsSubAverageItem';
import VehicleDetailTitledSection from 'components/Vehicle/VehicleDetail/VehicleDetailTitledSection';
import { useFirstRender } from 'hooks/useFirstRender';
import useTranslation from 'next-translate/useTranslation';
import { FunctionComponent, useState } from 'react';
import { experienceRatingService } from 'services/Experience/ExperienceRatingService';
import { useVehicleDetailCommentsModal } from 'states/useVehicleDetailCommentsModalState';
import { ExperienceDetail } from 'types/experience/experience-detail.type';
import { ExperienceRating } from 'types/experience/experience-rating.type';
import ExperienceDetailCommentModal from './ExperienceDetailCommentsModal';

interface ExperienceDetailCommentsProps {
  experience: ExperienceDetail;
}

const ExperienceDetailComments: FunctionComponent<
  ExperienceDetailCommentsProps
> = ({ experience }) => {
  const { t } = useTranslation();
  const { setCommentModalVisible } = useVehicleDetailCommentsModal();

  const [loading, setLoading] = useState<boolean>(false);
  const [comments, setComments] = useState<ExperienceRating[]>([]);
  const [totalElements, setTotalElements] = useState<number>(0);

  const getComments = async () => {
    try {
      setLoading(true);
      const res = await experienceRatingService.getExperienceRatings(
        experience.id,
        {
          page: 0,
          size: 3,
        }
      );
      setComments(res.content);
      setTotalElements(res.totalElements);
    } finally {
      setLoading(false);
    }
  };

  useFirstRender(() => {
    getComments();
  });

  return loading && !comments.length ? (
    <></>
  ) : (
    <VehicleDetailTitledSection
      className="mt-8 mb-16"
      title={
        <div className="flex">
          <RatingStar
            rating={experience.ratingAverageBundle.overallAverage || 0}
            commentCount={experience.ratingAverageBundle.reviewCount || 0}
          />
        </div>
      }
      noHeading
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <VehicleDetailCommentsSubAverageItem
          average={experience.ratingAverageBundle.hygieneAverage || 0}
          label={t('common:ratingCategory.hygiene')}
        />
        <VehicleDetailCommentsSubAverageItem
          average={experience.ratingAverageBundle.serviceAverage || 0}
          label={t('common:ratingCategory.service')}
        />
        <VehicleDetailCommentsSubAverageItem
          average={experience.ratingAverageBundle.communicationAverage || 0}
          label={t('common:ratingCategory.communication')}
        />
        <VehicleDetailCommentsSubAverageItem
          average={experience.ratingAverageBundle.pricePerformanceAverage || 0}
          label={t('common:ratingCategory.pricePerformance')}
        />
      </div>
      <div className="mt-6">
        <div className="flex flex-col gap-6">
          {comments.map((comment) => (
            <VehicleDetailCommentsItem
              comment={comment.content}
              // answer={undefined}
              commentDate={comment.commentDate}
              commentor={comment.commentor}
              title={comment.title}
              rating={comment.rateBundle.average}
              key={comment.commentId}
            />
          ))}
        </div>
        {totalElements > 3 ? (
          <div className="float-right">
            <BButton
              variant="secondary"
              priority="text"
              className="text-sm"
              onClick={() => setCommentModalVisible(true)}
            >
              {t('common:seeMore') + ' >'}
            </BButton>
          </div>
        ) : null}
      </div>
      <ExperienceDetailCommentModal experienceId={experience.id} />
    </VehicleDetailTitledSection>
  );
};

export default ExperienceDetailComments;
